import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Zap, Award } from "lucide-react";
import SiteLayout from "@/components/layout/SiteLayout";
import PageHero from "@/components/sections/PageHero";
import ContentSection from "@/components/sections/ContentSection";
import SplitSection from "@/components/sections/SplitSection";
import heroImage from "@/assets/community-impact-hero.jpg";
import lifestyleImage from "@/assets/about-lifestyle.jpg";
import exteriorImage from "@/assets/community-tropical-group.jpg";
import splitImage from "@/assets/community-giveback.jpg";
import ctaImage from "@/assets/community-home-cta.jpg";

const pillars = [
  {
    index: "01",
    eyebrow: "Educate",
    title: "Educating communities through access and opportunity",
    description:
      "We believe knowledge is the foundation of every strong decision. Every client we work with receives the guidance, resources, and clarity they need to move forward with confidence.",
    icon: BookOpen,
  },
  {
    index: "02",
    eyebrow: "Empower",
    title: "Empowering individuals and families to thrive",
    description:
      "We help clients take meaningful steps toward financial independence — through homeownership, investment, and building generational wealth in their communities.",
    icon: Zap,
  },
  {
    index: "03",
    eyebrow: "Elevate",
    title: "Elevating lives — locally and globally",
    description:
      "Our reach extends beyond Massachusetts. Every transaction creates a ripple of impact that touches families, nonprofits, and communities far beyond the closing table.",
    icon: Award,
  },
];

const CommunityImpact = () => {
  useEffect(() => {
    document.title = "Community Impact | Hadassah Homes";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "At Hadassah Homes, every transaction is an opportunity to create meaningful change. We donate a portion of every completed transaction to a nonprofit of your choice.");
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = "https://hadassahhomesinc.com/community-impact";
  }, []);

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Community Impact"
        title="Real Estate With Purpose"
        subtitle="Every transaction is more than a closing — it's an opportunity to create meaningful change."
        backgroundImage={heroImage}
      />

      {/* — SPLIT INTRO — */}
      <ContentSection>
        <SplitSection
          title="We Give Back With Every Closing"
          description="At Hadassah Homes Real Estate & Design, Inc., we proudly donate a portion of every completed transaction to a nonprofit organization of your choice."
          image={splitImage}
          imageAlt="Massachusetts neighborhood served by Hadassah Homes"
          reverse
        >
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Whether supporting housing initiatives, youth development, education, or global outreach — your home purchase or sale becomes part of something greater.
          </p>
          <div className="pt-2">
            <Link
              to="/consultation"
              className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.12em] text-accent hover:text-accent/80 transition-colors"
            >
              Start your journey <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </SplitSection>
      </ContentSection>

      {/* — MISSION STATEMENT — */}
      <section className="relative overflow-hidden bg-[#0a1f18] py-28 md:py-36">
        <img
          src={lifestyleImage}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover opacity-[0.04]"
          loading="lazy"
        />
        <div className="relative z-10 mx-auto max-w-[1520px] px-6 md:px-12 lg:px-16">
          <div className="max-w-[54rem] mx-auto text-center space-y-7 md:space-y-9">
            <span className="font-body text-xs uppercase tracking-[0.14em] text-accent">Our Mission</span>
            <h2 className="font-headline text-4xl md:text-[3.65rem] lg:text-[4.5rem] text-secondary-foreground leading-[1.02]">
              Simple Yet Powerful
            </h2>
            <p className="text-white/85 text-base md:text-lg leading-[1.9] max-w-3xl mx-auto">
              This initiative reflects our commitment to not only serving our clients, but also uplifting the communities we touch. When you work with Hadassah Homes, your transaction creates a ripple of impact far beyond the closing table.
            </p>
            <div className="mx-auto w-16 h-px bg-accent/50" />
            <p className="font-headline text-2xl md:text-3xl text-accent/90 leading-snug italic max-w-3xl mx-auto">
              "To educate, empower, and elevate."
            </p>
          </div>
        </div>
      </section>

      {/* — THREE PILLARS (GLASS CARDS) — */}
      <section className="relative overflow-hidden border-y border-[#C9A95C]/15 bg-[#173D2D] py-24 md:py-32">
        <div className="pointer-events-none absolute -right-20 top-10 h-72 w-72 bg-[radial-gradient(circle,rgba(201,169,92,0.14)_0%,rgba(201,169,92,0.05)_34%,transparent_74%)]" />
        <div className="pointer-events-none absolute -left-20 bottom-10 h-64 w-64 bg-[radial-gradient(circle,rgba(201,169,92,0.10)_0%,rgba(201,169,92,0.03)_34%,transparent_74%)]" />
        <div className="mx-auto max-w-[1440px] px-5 md:px-10 lg:px-14">
          <div className="mx-auto mb-16 max-w-4xl space-y-5 text-center md:mb-20">
            <p className="font-body text-xs font-medium uppercase tracking-[0.22em] text-[#C9A95C]">
              Together, We Are
            </p>
            <h2 className="font-headline text-4xl leading-tight tracking-tight text-white md:text-5xl">
              More Than Real Estate
            </h2>
            <p className="mx-auto max-w-2xl leading-relaxed text-white/80 md:text-lg">
              Three principles guide everything we do — for our clients and the communities we serve.
            </p>
          </div>

          <div className="grid grid-cols-1 items-stretch gap-6 md:grid-cols-3 lg:gap-8">
            {pillars.map((item) => (
              <article
                key={item.title}
                className="h-full border border-white/15 bg-white/[0.055] p-6 text-white transition-colors duration-300 hover:bg-white/[0.08] md:p-8"
              >
                <div className="space-y-5">
                  <div className="flex items-start justify-between gap-6 border-t border-[#C9A95C]/45 pt-4">
                    <span className="font-body text-xs uppercase tracking-[0.18em] text-[#C9A95C]">
                      {item.eyebrow}
                    </span>
                    <span className="font-body text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A95C]">
                      {item.index}
                    </span>
                  </div>
                  <div className="space-y-3">
                    <item.icon className="h-6 w-6 text-[#C9A95C]" strokeWidth={1.5} aria-hidden />
                    <h3 className="font-headline text-xl leading-tight text-white md:text-2xl">
                      {item.title}
                    </h3>
                    <p className="text-base leading-relaxed text-white/78 md:text-lg">
                      {item.description}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* — FULL-WIDTH IMAGE BREAK — */}
      <div className="relative h-[40vh] md:h-[52vh] overflow-hidden">
        <img
          src={exteriorImage}
          alt="Massachusetts neighborhood served by Hadassah Homes"
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/20" />
      </div>

      {/* — HOW IT WORKS — */}
      <ContentSection>
        <div className="grid grid-cols-1 lg:grid-cols-[0.44fr_0.56fr] gap-12 md:gap-16 lg:gap-20 items-start">
          <div className="space-y-6 md:space-y-8">
            <div>
              <span className="font-body text-[10px] uppercase tracking-[0.4em] text-accent mb-4 block">Our Initiative</span>
              <h2 className="font-headline text-4xl md:text-5xl text-foreground leading-tight">How It Works</h2>
            </div>
            <p className="text-muted-foreground text-base md:text-lg leading-[1.8]">
              Giving back is built directly into how we do business. No extra steps. No minimum spend. Every completed transaction automatically creates an opportunity to direct impact.
            </p>
            <div className="border-l border-accent/55 pl-5 md:pl-6">
              <p className="font-headline text-xl md:text-2xl text-foreground leading-[1.3]">
                Your real estate decision becomes a force for good.
              </p>
            </div>
          </div>

          <div className="space-y-10 md:space-y-12 border-l border-accent/22 pl-6 md:pl-8">
            {[
              {
                step: "Step 01",
                title: "You complete a transaction with Hadassah Homes",
                detail: "Whether buying, selling, or investing — every closed deal activates our giving initiative.",
              },
              {
                step: "Step 02",
                title: "You choose the nonprofit",
                detail: "Select any registered nonprofit that matters to you — local or global, large or small.",
              },
              {
                step: "Step 03",
                title: "We donate a portion on your behalf",
                detail: "A percentage of our commission is directed to the organization of your choice. Simple and transparent.",
              },
            ].map((item, index, arr) => (
              <div
                key={item.step}
                className={index < arr.length - 1 ? "border-b border-border/55 pb-10 md:pb-12" : ""}
              >
                <p className="font-body text-[10px] uppercase tracking-[0.18em] text-accent mb-3">
                  {item.step}
                </p>
                <h3 className="font-headline text-[1.75rem] md:text-[2rem] text-foreground leading-[1.2] mb-3">
                  {item.title}
                </h3>
                <p className="max-w-xl text-muted-foreground text-base leading-[1.8]">
                  {item.detail}
                </p>
              </div>
            ))}
          </div>
        </div>
      </ContentSection>

      {/* — CLOSING FULL-BLEED CTA — */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <img
          src={ctaImage}
          alt="Hadassah Homes community impact"
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/85 via-black/60 to-black/30" />
        <div className="absolute inset-0 z-10 bg-gradient-to-r from-black/80 via-black/50 to-black/20" />
        <div className="relative z-20 mx-auto max-w-[1440px] px-5 md:px-12 text-center">
          <div className="mx-auto max-w-3xl space-y-6 md:space-y-7">
            <p className="font-body text-[10px] uppercase tracking-[0.22em] text-accent">
              Invest in More Than Property
            </p>
            <blockquote className="font-headline text-3xl sm:text-4xl md:text-5xl leading-tight text-white">
              "When you work with Hadassah Homes, you're not just investing in real estate — you're investing in impact."
            </blockquote>
            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row pt-4">
              <Link
                to="/consultation"
                className="inline-flex min-w-[250px] items-center justify-center font-body text-sm uppercase tracking-[0.12em] px-10 py-4 bg-accent text-white hover:bg-accent/90 transition-colors"
              >
                Start Your Journey
              </Link>
              <Link
                to="/about"
                className="inline-flex min-w-[250px] items-center justify-center border border-white/40 px-10 py-4 font-body text-sm uppercase tracking-[0.12em] text-white transition-colors hover:bg-white/10 hover:border-white/60"
              >
                Learn About Our Story
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default CommunityImpact;
